// ============================================================================
// REQUEST SERVICE - Digital Approval System
// ============================================================================
// Business logic and API calls for Request module

import { get, post, put, patch, del } from '../client/axiosClient';
import { requestEndpoints } from '../endpoints';
import type {
  ApiResponse,
  EmptyResponse,
  ID,
  Request,
  RequestDetails,
  CreateRequestRequest,
  UpdateRequestRequest,
  RequestQueryParameters,
  RequestListResponse,
  UpdateRequestStatusRequest,
  RequestNumberValidationResponse,
} from '../types';

/**
 * Helper function to append query params to an already-built endpoint
 */
const appendQueryParams = (endpoint: string, params: Record<string, any>): string => {
  const queryString = new URLSearchParams(
    Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  return queryString ? `${endpoint}?${queryString}` : endpoint;
};

/**
 * Request Service Class
 */
class RequestService {
  /**
   * Get all requests with pagination and filtering
   */
  async getAll(params?: RequestQueryParameters): Promise<RequestListResponse> {
    // Use requestEndpoints.list() which already includes version prefix
    // Then append query params directly (don't use buildEndpointWithQuery which would double-build)
    const baseEndpoint = requestEndpoints.list();
    const endpoint = params ? appendQueryParams(baseEndpoint, params) : baseEndpoint;

    const response = await get<ApiResponse<RequestListResponse>>(endpoint);
    return response.data.data;
  }

  /**
   * Get request by ID
   */
  async getById(id: ID): Promise<Request> {
    const response = await get<ApiResponse<Request>>(requestEndpoints.getById(id));
    return response.data.data;
  }

  /**
   * Get complete request details including approvers, attachments, and related tickets
   */
  async getDetails(id: ID): Promise<RequestDetails> {
    const response = await get<ApiResponse<RequestDetails>>(requestEndpoints.getDetails(id));
    return response.data.data;
  }

  /**
   * Get request by request number
   */
  async getByNumber(requestNumber: string): Promise<Request> {
    const response = await get<ApiResponse<Request>>(
      requestEndpoints.getByNumber(requestNumber)
    );
    return response.data.data;
  }

  /**
   * Lookup requests by request number OR external reference number.
   * Returns an array of summary results (0, 1, or many).
   */
  async lookup(term: string): Promise<Request[]> {
    const response = await get<ApiResponse<Request[]>>(requestEndpoints.lookup(term));
    return response.data.data;
  }

  /**
   * Update the external reference number on a request (das_admin only).
   * Pass null to clear the value.
   */
  async updateExternalReference(id: ID, externalReferenceNo: string | null): Promise<Request> {
    const response = await patch<ApiResponse<Request>>(
      requestEndpoints.updateExternalReference(id),
      { externalReferenceNo }
    );
    return response.data.data;
  }

  /**
   * Get requests by status
   */
  async getByStatus(status: string): Promise<Request[]> {
    const response = await get<ApiResponse<Request[]>>(requestEndpoints.getByStatus(status));
    return response.data.data;
  }

  /**
   * Get current user's requests
   */
  async getMyRequests(): Promise<Request[]> {
    const response = await get<ApiResponse<Request[]>>(requestEndpoints.getMyRequests());
    return response.data.data;
  }

  /**
   * Get requests by request type
   */
  async getByRequestType(requestTypeId: ID): Promise<Request[]> {
    const response = await get<ApiResponse<Request[]>>(
      requestEndpoints.getByRequestType(requestTypeId)
    );
    return response.data.data;
  }

  /**
   * Create new request
   */
  async create(data: CreateRequestRequest): Promise<Request> {
    const response = await post<ApiResponse<Request>>(requestEndpoints.create(), data);
    return response.data.data;
  }

  /**
   * Update request
   */
  async update(id: ID, data: UpdateRequestRequest): Promise<Request> {
    const response = await put<ApiResponse<Request>>(requestEndpoints.update(id), data);
    return response.data.data;
  }

  /**
   * Submit draft request
   */
  async submit(id: ID): Promise<Request> {
    const response = await post<ApiResponse<Request>>(requestEndpoints.submit(id), {});
    return response.data.data;
  }

  /**
   * Update request status
   */
  async updateStatus(id: ID, data: UpdateRequestStatusRequest): Promise<{ success: boolean }> {
    const response = await patch<ApiResponse<{ success: boolean }>>(
      requestEndpoints.updateStatus(id),
      data
    );
    return response.data.data;
  }

  /**
   * Delete request
   */
  async delete(id: ID): Promise<{ success: boolean }> {
    const response = await del<ApiResponse<{ success: boolean }>>(requestEndpoints.delete(id));
    return response.data.data;
  }

  /**
   * Validate request number uniqueness
   */
  async validateNumber(
    requestNumber: string,
    excludeId?: ID
  ): Promise<RequestNumberValidationResponse> {
    const response = await get<ApiResponse<RequestNumberValidationResponse>>(
      requestEndpoints.validateNumber(requestNumber, excludeId)
    );
    return response.data.data;
  }

  /**
   * Get requests with custom filters (helper method)
   */
  async search(params: RequestQueryParameters): Promise<RequestListResponse> {
    return this.getAll(params);
  }

  /**
   * Get draft requests for current user
   */
  async getMyDrafts(): Promise<Request[]> {
    const allRequests = await this.getMyRequests();
    return allRequests.filter((req) => req.currentStatus === 'Draft');
  }

  /**
   * Get submitted requests for current user
   */
  async getMySubmitted(): Promise<Request[]> {
    const allRequests = await this.getMyRequests();
    return allRequests.filter((req) => req.currentStatus === 'Submitted');
  }

  /**
   * Get requests pending approval
   */
  async getPending(): Promise<Request[]> {
    return this.getByStatus('In Review');
  }
}

// Export singleton instance
export const requestService = new RequestService();
export default requestService;
